import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  useTheme,
  alpha,
  InputAdornment,
  Skeleton,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const PlayerMarketsDataTable = ({
  marketData = [],
  monthlyData = [],
  loading = false,
  monthlyLoading = false,
  error = null,
  dataIndex = null,
  title = "Player Markets Data",
  subtitle = "",
  initialMonth = null,
  initialSearch = null,
  initialOrderBy = null,
  initialOrder = null,
  onFilterChange = () => {},
}) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState(initialOrderBy || "item_type");
  const [order, setOrder] = useState(initialOrder || "asc");
  const [searchQuery, setSearchQuery] = useState(initialSearch || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearch || "");
  const [selectedMonth, setSelectedMonth] = useState({ label: "ALL TIME", value: "ALL_TIME" });
  const [availableMonths, setAvailableMonths] = useState([{ label: "ALL TIME", value: "ALL_TIME" }]);

  // Track if component has completed initialization to avoid calling onFilterChange on mount
  const [isInitialized, setIsInitialized] = useState(false);

  // With lazy tab rendering and parsed data caching, no artificial delay is needed
  // Data is ready immediately from cache or loads quickly on first visit
  const isReadyToRender = !loading && dataIndex !== null;

  // Extract available months when monthlyData changes
  useEffect(() => {
    if (monthlyData.length > 0) {
      const uniqueMonths = [...new Set(monthlyData.map((item) => item.month))]
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));

      const monthOptions = [
        { label: "ALL TIME", value: "ALL_TIME" },
        ...uniqueMonths.map((month) => ({ label: month, value: month })),
      ];
      setAvailableMonths(monthOptions);

      // Set initial selected month from URL if available
      if (initialMonth) {
        const foundMonth = monthOptions.find(
          (opt) => opt.value === initialMonth
        );
        if (foundMonth) {
          setSelectedMonth(foundMonth);
        }
      }
    }
  }, [monthlyData, initialMonth]);

  // Mark initialization as complete after first render with delay to prevent URL flickering
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Debounce search query to reduce filter calculations during rapid typing
  // Only updates debounced state, not URL
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleMonthChange = (event, newValue) => {
    if (newValue === null) {
      setSelectedMonth({ label: "ALL TIME", value: "ALL_TIME" });
      if (isInitialized) {
        onFilterChange({ month: "ALL_TIME", search: searchQuery, orderby: orderBy, order: order });
      }
    } else {
      setSelectedMonth(newValue);
      if (isInitialized) {
        onFilterChange({ month: newValue.value, search: searchQuery, orderby: orderBy, order: order });
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);

    // Update URL with sorting params
    if (isInitialized) {
      onFilterChange({ month: selectedMonth.value, search: searchQuery, orderby: property, order: newOrder });
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    // Debounce the URL update
    if (isInitialized) {
      setTimeout(() => {
        onFilterChange({ month: selectedMonth.value, search: value, orderby: orderBy, order: order });
      }, 500);
    }
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  // Memoize filtered data to prevent recalculation on every keystroke
  // Only recalculates when actual dependencies change (not on every render)
  const filteredData = useMemo(() => {
    // Determine which data source to use based on selected month
    // Uses pre-built index if available (O(1) lookup), falls back to filtering (O(n))
    const getActiveData = () => {
      const monthKey = selectedMonth.value === "ALL_TIME" ? "ALL_TIME" : selectedMonth.value;

      // Use index if available (already filtered for empty item_type)
      if (dataIndex && dataIndex[monthKey]) {
        return dataIndex[monthKey];
      }

      // Fallback: filter data directly (for initial render before index is ready)
      if (selectedMonth.value === "ALL_TIME") {
        return marketData.filter(
          (item) => item.item_type && item.item_type.toString().trim() !== ""
        );
      }
      return monthlyData
        .filter((item) => item.month === selectedMonth.value)
        .filter((item) => item.item_type && item.item_type.toString().trim() !== "");
    };

    return getActiveData()
      // Apply search filter (using debounced value for performance)
      .filter((item) => {
        if (!debouncedSearchQuery) return true;
        const query = debouncedSearchQuery.toLowerCase();
        return (
          item.item_type?.toLowerCase().includes(query) ||
          item.top_buyer?.toLowerCase().includes(query) ||
          item.top_seller?.toLowerCase().includes(query)
        );
      });
  }, [debouncedSearchQuery, dataIndex, selectedMonth.value, marketData, monthlyData]);

  // Memoize sorted data separately to avoid sorting on every render
  const sortedData = useMemo(() => {
    // Create a copy to avoid mutating the memoized filteredData
    return [...filteredData].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];

      // Handle numeric values
      if (!isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal))) {
        return order === "desc"
          ? parseFloat(bVal) - parseFloat(aVal)
          : parseFloat(aVal) - parseFloat(bVal);
      }

      // Handle string values
      const comparison = (aVal?.toString() || "").localeCompare(bVal?.toString() || "");
      return order === "desc" ? -comparison : comparison;
    });
  }, [filteredData, orderBy, order]);

  // Limit displayed rows for performance (show first 100)
  const displayedData = useMemo(() => {
    return sortedData.slice(0, 100);
  }, [sortedData]);

  if (error) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <StorefrontIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h5">{title}</Typography>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <StorefrontIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h5">{title}</Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <Box>
        <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Autocomplete
            id="month-filter"
            options={availableMonths}
            value={selectedMonth}
            onChange={handleMonthChange}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            sx={{ minWidth: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Time Period"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <CalendarMonthIcon sx={{ color: "action.active", mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Search"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by item, buyer, seller..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 250,
              flexGrow: 1,
              maxWidth: 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
          {selectedMonth.value !== "ALL_TIME" && (
            <Chip
              label={`Viewing: ${selectedMonth.label}`}
              color="primary"
              variant="outlined"
              size="medium"
            />
          )}
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            boxShadow: "none",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
            overflow: "auto",
          }}
        >
          <Table stickyHeader aria-label="markets data table">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  "& th": {
                    fontWeight: "bold",
                  },
                }}
              >
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "item_type"}
                    direction={orderBy === "item_type" ? order : "asc"}
                    onClick={createSortHandler("item_type")}
                  >
                    Item Type
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "avg_price_per_unit"}
                    direction={orderBy === "avg_price_per_unit" ? order : "asc"}
                    onClick={createSortHandler("avg_price_per_unit")}
                  >
                    Avg Price Per Unit
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "total_sales"}
                    direction={orderBy === "total_sales" ? order : "asc"}
                    onClick={createSortHandler("total_sales")}
                  >
                    Total Sales
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "top_buyer"}
                    direction={orderBy === "top_buyer" ? order : "asc"}
                    onClick={createSortHandler("top_buyer")}
                  >
                    Top Buyer
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "top_buyer_amount"}
                    direction={orderBy === "top_buyer_amount" ? order : "asc"}
                    onClick={createSortHandler("top_buyer_amount")}
                  >
                    Top Buyer Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "top_seller"}
                    direction={orderBy === "top_seller" ? order : "asc"}
                    onClick={createSortHandler("top_seller")}
                  >
                    Top Seller
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "top_seller_amount"}
                    direction={orderBy === "top_seller_amount" ? order : "asc"}
                    onClick={createSortHandler("top_seller_amount")}
                  >
                    Top Seller Amount
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || !isReadyToRender
                ? Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {Array.from(new Array(7)).map((_, cellIndex) => (
                        <TableCell key={`cell-${cellIndex}`}>
                          <Skeleton animation="wave" height={24} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : displayedData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.02
                            ),
                          },
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.05
                            ),
                          },
                          transition: "background-color 0.2s",
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Chip
                              size="small"
                              label={row.item_type}
                              sx={{
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                                color: theme.palette.primary.main,
                                fontWeight: "bold",
                                fontSize: 12,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {!isNaN(parseFloat(row.avg_price_per_unit))
                            ? parseFloat(row.avg_price_per_unit).toFixed(2)
                            : row.avg_price_per_unit}
                        </TableCell>
                        <TableCell align="right">{row.total_sales}</TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            <ShoppingCartIcon
                              fontSize="small"
                              sx={{
                                mr: 0.5,
                                color: theme.palette.success.main,
                              }}
                            />
                            {row.top_buyer}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {row.top_buyer_amount}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            <StorefrontIcon
                              fontSize="small"
                              sx={{ mr: 0.5, color: theme.palette.info.main }}
                            />
                            {row.top_seller}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {row.top_seller_amount}
                        </TableCell>
                      </TableRow>
                    ))}
              {!loading && filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 3 }}>
                      No market data found matching your search criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {loading || !isReadyToRender
              ? "Loading data..."
              : sortedData.length > 100
                ? `Showing first 100 of ${sortedData.length} matches`
                : `Showing ${sortedData.length} items`}
          </Typography>
          <Chip
            label={loading || !isReadyToRender ? "..." : `${sortedData.length} items`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PlayerMarketsDataTable;
